let telegramBackHandler = null;

const parseTelegramVersion = (version) => {
    if (!version || typeof version !== 'string') return null;
    const [major, minor = '0'] = version.split('.');
    const parsedMajor = parseInt(major, 10);
    const parsedMinor = parseInt(minor, 10);
    if (Number.isNaN(parsedMajor) || Number.isNaN(parsedMinor)) return null;
    return { major: parsedMajor, minor: parsedMinor };
};

const isTelegramVersionAtLeast = (webApp, requiredMajor, requiredMinor = 0) => {
    const parsed = parseTelegramVersion(webApp?.version);
    if (!parsed) return false;
    if (parsed.major > requiredMajor) return true;
    if (parsed.major < requiredMajor) return false;
    return parsed.minor >= requiredMinor;
};

export const setTelegramBackHandler = (handler) => {
    telegramBackHandler = typeof handler === 'function' ? handler : null;
};

export function updateTelegramBackButtonVisibility(webApp) {
    try {
        const wa = webApp || (typeof window !== 'undefined' ? window.Telegram?.WebApp : null);
        if (!isTelegramVersionAtLeast(wa, 6, 1)) return;
        const bb = wa?.BackButton;
        if (!bb) return;
        const pathname = typeof window !== 'undefined' ? (window.location?.pathname || '') : '';
        const isHome = (
            pathname === '/'
        );
        if (isHome) {
            if (typeof bb.hide === 'function') bb.hide();
        } else {
            if (typeof bb.show === 'function') bb.show();
        }
    } catch {}
}

let __tgBackSyncInstalled = false;
export function ensureTelegramBackVisibilitySync(webApp) {
    if (typeof window === 'undefined') return;
    if (__tgBackSyncInstalled) return;
    __tgBackSyncInstalled = true;
    const wa = webApp || (typeof window !== 'undefined' ? window.Telegram?.WebApp : null);
    const update = () => {
        try { updateTelegramBackButtonVisibility(wa); } catch {}
    };
    const onPopstate = () => {
        try { update(); } catch {}
    };
    try { window.addEventListener('popstate', onPopstate); } catch {}
    try {
        const origPush = window.history.pushState?.bind(window.history);
        if (origPush) {
            window.history.pushState = function (...args) {
                const r = origPush(...args);
                try { update(); } catch {}
                return r;
            };
        }
    } catch {}
}

export const handleTelegramBackClick = (webApp) => {
    console.log('[handleTelegramBackClick]', { webApp });
    try {
        if (typeof telegramBackHandler === 'function') {
            const handled = telegramBackHandler();
            if (handled !== false) return;
        }
        if (typeof window !== 'undefined' && window.history) {
            try { window.history.back(); return; } catch {}
        }
    } catch {
        
    }
};

export function openProjectInBrowserWithToken(publicUrl) {
    console.log('[openProjectInBrowserWithToken]', {
        url: publicUrl,
        hasWebApp: !!window?.Telegram?.WebApp,
        hasOpenLink: !!window?.Telegram?.WebApp?.openLink,
        userAgent: navigator.userAgent
    });
    
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    
    let urlWithTokens = publicUrl;
    if (accessToken) {
        const separator = publicUrl.includes('?') ? '&' : '?';
        urlWithTokens = `${publicUrl}${separator}token=${accessToken}`;
        if (refreshToken) {
            urlWithTokens += `&refresh_token=${refreshToken}`;
        }
    }
    
    console.log('[openProjectInBrowserWithToken] Opening with tokens', { 
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken
    });
    
    if (window?.Telegram?.WebApp?.openLink) {
        console.log('[openProjectInBrowserWithToken] Using Telegram WebApp.openLink');
        window.Telegram.WebApp.openLink(urlWithTokens);
    } else {
        console.log('[openProjectInBrowserWithToken] Using window.open fallback');
        window.open(urlWithTokens, '_blank');
    }
}

export function isTelegramWebApp() {
    if (typeof window === 'undefined') return false;
    const wa = window.Telegram?.WebApp;
    // true if Telegram WebApp object exists and we have any init data (DEV may be empty)
    return !!wa && (typeof wa.initData === 'string' ? wa.initData !== '' : true);
}

export function isMobile() {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent.toLowerCase();
    return /android|iphone|ipad|mobile|webos|blackberry|iemobile|opera mini/.test(ua);
}

export function isDesktop() {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent.toLowerCase();
    
    const isDesktopPlatform = /mac|win|linux|x11|cros/.test(ua);
    const isMobile = /android|iphone|ipad|mobile|webos|blackberry|iemobile|opera mini/.test(ua);
    
    return (isDesktopPlatform && !isMobile) || /tdesktop/.test(ua);
}

export const setupMobileViewport = () => {
    if (typeof document === 'undefined') return;
    
    try {
        // Add mobile-specific CSS classes
        document.documentElement.classList.add('telegram-webapp');
        
        if (isMobile()) {
            document.documentElement.classList.add('mobile-device');
            
            // Force viewport settings for iPhone
            const viewport = document.querySelector('meta[name="viewport"]');
            if (viewport) {
                viewport.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no, maximum-scale=1');
            }
            
            // Add iOS-specific meta tags if needed
            if (/iphone|ipad/.test(navigator.userAgent.toLowerCase())) {
                // Prevent zoom on input focus (common iOS issue)
                const addMetaTag = (name, content) => {
                    let meta = document.querySelector(`meta[name="${name}"]`);
                    if (!meta) {
                        meta = document.createElement('meta');
                        meta.name = name;
                        document.head.appendChild(meta);
                    }
                    meta.content = content;
                };
                
                addMetaTag('apple-mobile-web-app-capable', 'yes');
                addMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent');
                addMetaTag('apple-touch-fullscreen', 'yes');
            }
        }
    } catch (e) {
        console.warn('Failed to setup mobile viewport:', e);
    }
};

export const setupMobileFullscreenPadding = () => {
    if (typeof document === 'undefined') return;
    
    try {
        const webApp = window.Telegram?.WebApp;
        if (!webApp) return;
        
        // Only apply on mobile devices
        if (!isMobile()) return;
        
        // Calculate fixed top offset for mobile
        let topOffset = 64; // Default for Android and other mobile devices
        
        // For iPhone, use larger padding for notch/status bar + TG controls
        if (/iphone|ipad/.test(navigator.userAgent.toLowerCase())) {
            topOffset = 88; // iPhone safe area + TG controls
        }
        
        // Apply CSS variables once
        document.documentElement.style.setProperty('--telegram-top-offset', `${topOffset}px`);
        
        // Apply fixed padding-top directly to body — covers ALL content
        document.body.style.paddingTop = `${topOffset}px`;
        document.body.style.boxSizing = 'border-box';
        
        // Add CSS classes
        document.documentElement.classList.add('mobile-fullscreen');
        document.documentElement.classList.add('telegram-expanded');
        
        console.log('[Telegram WebApp] Fixed mobile padding applied:', {
            topOffset,
            device: /iphone|ipad/.test(navigator.userAgent.toLowerCase()) ? 'iOS' : 'Android'
        });
        
    } catch (e) {
        console.warn('Failed to setup mobile fullscreen padding:', e);
    }
};

export const initTelegramWebApp = () => {
    if (!window.Telegram?.WebApp) return;

    const webApp = window.Telegram.WebApp;

    // Setup mobile viewport first
    setupMobileViewport();
    setupMobileFullscreenPadding();

    // Guard setHeaderColor for older versions (warned in logs for 6.0). If version property exists, require >= 6.1
    const canSetHeader = isTelegramVersionAtLeast(webApp, 6, 1);
    const canUseBackButton = isTelegramVersionAtLeast(webApp, 6, 1);
    const canSetBottomBarColor = isTelegramVersionAtLeast(webApp, 6, 1);

    if (canSetHeader && typeof webApp.setHeaderColor === 'function') {
        const tp = webApp.themeParams || {};
        const headerColor = tp.bg_color || '#14171D';
        try { webApp.setHeaderColor(headerColor); } catch {}
    }
    webApp.ready();

    // Always expand on mobile devices for fullscreen experience
    if (isMobile()) {
        // On mobile, always try to expand to fullscreen with retry mechanism
        const tryExpand = (attempt = 0) => {
            if (typeof webApp.expand === 'function') {
                try {
                    webApp.expand();
                    console.log('[Telegram WebApp] Expanded on mobile, attempt:', attempt + 1);
                } catch (e) {
                    console.warn('Failed to expand Telegram WebApp on mobile:', e);
                    // Retry up to 3 times with delays
                    if (attempt < 2) {
                        setTimeout(() => tryExpand(attempt + 1), 100 * (attempt + 1));
                    }
                }
            }
        };
        
        // Initial expansion attempt
        tryExpand();
        
        // Additional mobile-specific settings
        try {
            // Set web app to full viewport on mobile
            if (typeof webApp.requestFullscreen === 'function') {
                webApp.requestFullscreen();
            }
        } catch (e) {
            // Ignore if requestFullscreen is not available
        }
        
        // Force expansion after a short delay to ensure it works
        setTimeout(() => {
            if (typeof webApp.expand === 'function' && !webApp.isExpanded) {
                try {
                    webApp.expand();
                    console.log('[Telegram WebApp] Forced expansion on mobile after delay');
                } catch (e) {
                    console.warn('Failed to force expand Telegram WebApp on mobile:', e);
                }
            }
        }, 500);
    } else {
        // On desktop, only expand if not already expanded
        if (!webApp.isExpanded && typeof webApp.expand === 'function') {
            try {
                webApp.expand();
            } catch (e) {
                console.warn('Failed to expand Telegram WebApp on desktop:', e);
            }
        }
    }
    try {
        const bb = webApp.BackButton;
        if (canUseBackButton && bb) {
            if (typeof bb.onClick === 'function') {
                bb.onClick(() => {
                    handleTelegramBackClick(webApp);
                });
            }
            updateTelegramBackButtonVisibility(webApp);
            ensureTelegramBackVisibilitySync(webApp);
        }
    } catch {}

    const applyBottomBarColor = () => {
        try {
            if (!canSetBottomBarColor) return;
            if (typeof webApp.setBottomBarColor !== 'function') return;
            const tp = webApp.themeParams || {};
            const bottomColor = tp.bg_color || '#14171D';
            webApp.setBottomBarColor(bottomColor);
        } catch {}
    };
    applyBottomBarColor();
};

export const getTelegramUser = () => {
    if (!window.Telegram?.WebApp?.initDataUnsafe?.user) return null;
    return window.Telegram.WebApp.initDataUnsafe.user;
};

export const getTelegramThemeParams = () => {
    if (typeof window === 'undefined') return null;
    const wa = window.Telegram?.WebApp;
    if (!wa) return null;
    const params = wa.themeParams || null;
    const colorScheme = wa.colorScheme;
    return params ? { ...params, colorScheme } : null;
};

export const onTelegramThemeChanged = (cb) => {
    if (typeof window === 'undefined') return () => {};
    const wa = window.Telegram?.WebApp;
    if (!wa || typeof wa.onEvent !== 'function') return () => {};
    const handler = () => {
        try {
            const params = wa.themeParams || null;
            try {
                const bg = params?.bg_color || '#14171D';
                if (isTelegramVersionAtLeast(wa, 6, 1) && typeof wa.setHeaderColor === 'function') wa.setHeaderColor(bg);
                if (isTelegramVersionAtLeast(wa, 6, 1) && typeof wa.setBottomBarColor === 'function') wa.setBottomBarColor(bg);
            } catch {}
            cb(params ? { ...params, colorScheme: wa.colorScheme } : null);
        } catch {}
    };
    wa.onEvent('themeChanged', handler);
    return () => {
        try {
            if (typeof wa.offEvent === 'function') {
                wa.offEvent('themeChanged', handler);
            }
        } catch {}
    };
};
